import React, { useState, useEffect, useCallback, useMemo } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import moment from "moment";
import { Loader } from "components/Loader";
import sessionHandling from "services/utils/notificationUtils";
import { Box } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useFetch } from "services/hooks/useFetch";
import { theme, optionalAvathar, defaultAllSelection } from "services/constants";
import { tooltipStyleProps } from "services/constants/Styles";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { FilterProvider } from "pages/FilterContext";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import Groups2Rounded from "@mui/icons-material/Groups2Rounded";
import ScheduleRounded from "@mui/icons-material/ScheduleRounded";
import TaskAltRounded from "@mui/icons-material/TaskAltRounded";
import FreeBreakfastRounded from "@mui/icons-material/FreeBreakfastRounded";
import { checkViewerPermission, subModuleNameMatches } from "services/utils/HelperFunction";
import { overtimeReportHeder } from "../TeamZoneHeaders";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { getThisMonthRange } from "components/CustomComponents/dateRangeDefaults";
import { CompactHeaderSelectFilter } from "components/CustomComponents/CompactHeaderSelectFilter";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { useExcelDownload } from "components/CustomComponents/useExcelDownload";
import {
  APP_BAR_OFFSET,
  designColors,
  designShell,
  paginationFooter,
  rowsPerPageOptions,
  StatCard,
  withDesignFont,
} from "services/constants/designTokens";
import {
  approveStickyCustomFilterBar,
  approveStickyFooterBar,
  approveTableBodyCell,
  approveTableHeadCell,
  ApproveTable,
} from "pages/TeamZone/Approve/approveUi";

const sumHhMmValues = (values) => {
  const totalMinutes = (values || []).reduce((sum, value) => {
    const match = /^(\d{1,4}):(\d{2})$/.exec(`${value ?? ""}`.trim());
    if (!match) return sum;
    return sum + Number(match[1]) * 60 + Number(match[2]);
  }, 0);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}:${String(minutes).padStart(2, "0")}`;
};

export const OvertimeReport = ({
  setShowReport,
  setReportHeaderActions,
  embeddedReportTypes,
  selectedReportType,
  onReportTypeChange,
}) => {
  const {
    domain,
    token,
    userDetails,
    topbarCompanyId,
    userScreenAccess,
    topbarCompanyName,
  } = useSelector((state) => state.tokenReducer);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [otRepDetails, setOtRepDetails] = useState([]);
  const [initialDateRange] = useState(() =>
    getThisMonthRange({ endAt: "yesterday" }),
  );
  const [startDate, setStartDate] = useState(initialDateRange.startDate);
  const [endDate, setEndDate] = useState(initialDateRange.endDate);
  const [dateRangeLabel, setDateRangeLabel] = useState(initialDateRange.label);
  const { result: departmentData } = useFetch(
    `${domain}get-department/?user_id=${userDetails.id}&company_id=${topbarCompanyId}`,
  );
  const [departmentName, setDepartmentName] = useState("All");
  const [departmentId, setDepartmentId] = useState(defaultAllSelection);
  const [otReportData, setOtReportData] = useState([]);
  const [customFilterHeight, setCustomFilterHeight] = useState(0);
  const [customFilterNode, setCustomFilterNode] = useState(null);
  const [footerNode, setFooterNode] = useState(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const customFilterRef = useCallback((node) => setCustomFilterNode(node), []);
  const footerRef = useCallback((node) => setFooterNode(node), []);
  const { downloadExcel, downloadPdf, downloadCsv } = useExcelDownload();

  const teamZoneReports = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "Reports",
  );

  // Real payloads return "Overtime Analysis" for this screen's
  // sub_module_name — subModuleNameMatches applies the same alias table
  // checkViewerPermission below already relies on, instead of a raw ===
  // that would silently land on undefined and send an invalid submodule id
  // when saving a view.
  const subModuleId = teamZoneReports?.submodules?.find((sub) =>
    subModuleNameMatches(sub.sub_module_name, "Overtime Report"),
  )?.sub_module_id;

  const check_access = checkViewerPermission(
    userScreenAccess?.module_details,
    "Reports",
    "Overtime Report",
  );

  const handleSavedViewChange = useCallback(() => {
    setPage(0);
  }, []);

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "overtime_report_saved_views",
    fullHeaders: overtimeReportHeder,
    screenAccess: check_access,
    onViewChange: handleSavedViewChange,
  });

  useEffect(() => {
    const fetchUsertaskDetails = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(
          `${domain}over-time-approve/?user_id=${userDetails.id}&department_id=${departmentId}&status=Approved&start_date=${startDate}&end_date=${endDate}&company_id=${topbarCompanyId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `token ${token}`,
            },
          },
        );
        const res = await response.json();
        if (response.ok) {
          setOtRepDetails(res);
          setOtReportData(res);
        } else if (response.status === 409) {
          setOtRepDetails([]);
          setOtReportData([]);
          sessionHandling();
        } else {
          setOtRepDetails([]);
          setOtReportData([]);
          throw new Error('REQUEST_FAILED');
        }
      } catch (error) {
        console.error('[Overtimereport] operation failed:', error);
        setOtRepDetails([]);
        setOtReportData([]);
        showToast("error", "Couldn't load the overtime report. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsertaskDetails();
  }, [
    domain,
    token,
    startDate,
    endDate,
    departmentId,
    userDetails.id,
    topbarCompanyId,
  ]);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStartDate = (rangeStart, rangeEnd, rangeLabel) => {
    const parsedStartDate = moment(rangeStart);
    const parsedEndDate = moment(rangeEnd);

    if (!parsedStartDate.isValid() || !parsedEndDate.isValid()) {
      return;
    }

    setStartDate(parsedStartDate.format("YYYY-MM-DD"));
    setEndDate(parsedEndDate.format("YYYY-MM-DD"));
    setDateRangeLabel(rangeLabel || "Custom Range");
    setPage(0);
  };

  const handleFilteredDataChange = useCallback(
    (filteredData) => {
      setOtReportData(filteredData);
      if (page * rowsPerPage >= filteredData.length) setPage(0);
    },
    [page, rowsPerPage],
  );

  const departmentList = useMemo(
    () => [
      {
        id: defaultAllSelection,
        label: "All",
        department_name: "All",
      },
      ...(departmentData?.map((obj) => ({
        ...obj,
        label: obj.department_name,
      })) ?? []),
    ],
    [departmentData],
  );

  const updateDepartmentName = (event, data) => {
    if (data && data?.label) {
      setDepartmentName(data.label);
      setDepartmentId(data.id);
      setPage(0);
    }
  };

  const getReportMetadata = useCallback(() => {
    const formattedStartDate = moment(
      startDate,
      ["DD/MM/YYYY", "YYYY-MM-DD"],
      true,
    ).format("DD/MM/YYYY");
    const formattedEndDate = moment(
      endDate,
      ["DD/MM/YYYY", "YYYY-MM-DD"],
      true,
    ).format("DD/MM/YYYY");

    const rows = otReportData || [];
    const uniqueEmployeeCount = new Set(
      rows.map((row) => row?.user_id || row?.employee_id).filter(Boolean),
    ).size;

    return {
      "Company Name": topbarCompanyName || "N/A",
      Department: departmentName || "All",
      "Date Range": `${formattedStartDate} to ${formattedEndDate}`,
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
      Employees: uniqueEmployeeCount,
      "OT Requests": rows.length,
      "Total OT Duration": sumHhMmValues(rows.map((row) => row?.overtime_duration)),
      "Total Approved Duration": sumHhMmValues(
        rows.map((row) => row?.final_approved_duration),
      ),
      "Total Break Duration": sumHhMmValues(
        rows.map((row) => row?.overtime_break_duration),
      ),
    };
  }, [departmentName, endDate, otReportData, startDate, topbarCompanyName]);

  const handleOvertimeDownload = useCallback(
    async (exportRows, onProgress, downloadFormat = "excel") => {
      const rows = Array.isArray(exportRows) ? exportRows : [];
      const summaryRow = {
        rowType: "grand_total",
        user_name: "TOTAL",
        overtime_break_duration: sumHhMmValues(
          rows.map((row) => row?.overtime_break_duration),
        ),
        overtime_duration: sumHhMmValues(rows.map((row) => row?.overtime_duration)),
        final_approved_duration: sumHhMmValues(
          rows.map((row) => row?.final_approved_duration),
        ),
      };

      const exportToFormat =
        downloadFormat === "pdf"
          ? downloadPdf
          : downloadFormat === "csv"
            ? downloadCsv
            : downloadExcel;

      await exportToFormat(
        [...rows, summaryRow],
        visibleHeaders,
        "Overtime Report",
        {},
        getReportMetadata(),
        [],
      );

      if (typeof onProgress === "function") {
        onProgress(100);
      }
    },
    [downloadCsv, downloadExcel, downloadPdf, getReportMetadata, visibleHeaders],
  );

  // Same StatCard row Working Hours' Summary/Detailed/Time Card reports show
  // above their table — this report never had one, so it's built fresh here
  // from the currently visible (searched/filtered) rows, matching how those
  // reports derive their own cards from filtered rather than raw data.
  const overtimeSummaryCards = useMemo(() => {
    const rows = otReportData || [];
    const uniqueEmployeeCount = new Set(
      rows.map((row) => row?.user_id || row?.employee_id).filter(Boolean),
    ).size;

    return [
      {
        key: "employees",
        title: "Employees",
        value: uniqueEmployeeCount,
        icon: Groups2Rounded,
        accent: "#1D4ED8",
        tint: "#EEF4FF",
      },
      {
        key: "otHours",
        title: "Total OT Duration",
        value: sumHhMmValues(rows.map((row) => row?.overtime_duration)),
        icon: ScheduleRounded,
        accent: "#F97316",
        tint: "#FFF7ED",
      },
      {
        key: "approvedHours",
        title: "Total Approved Duration",
        value: sumHhMmValues(rows.map((row) => row?.final_approved_duration)),
        icon: TaskAltRounded,
        accent: "#7C3AED",
        tint: "#F5F3FF",
      },
      {
        key: "breakHours",
        title: "Total Break Duration",
        value: sumHhMmValues(rows.map((row) => row?.overtime_break_duration)),
        icon: FreeBreakfastRounded,
        accent: "#EF4444",
        tint: "#FEF2F2",
      },
    ];
  }, [otReportData]);

  const selectedReportTypeOption =
    embeddedReportTypes?.find(
      (option) =>
        option?.label === selectedReportType ||
        option?.value === selectedReportType,
    ) || null;

  const headerFilterControls = useMemo(
    () => (
      <Mui.Stack
        direction="row"
        spacing={1.5}
        useFlexGap
        flexWrap="wrap"
        justifyContent="flex-start"
        sx={{
          width: "100%",
          alignItems: "center",
        }}
      >
        <ApprovalDateRangeFilter
          thisMonthToDate
          startDate={startDate}
          endDate={endDate}
          dateRangeLabel={dateRangeLabel}
          onChange={handleStartDate}
          showToday={false}
          includeCurrentDate={false}
          allowFutureDate={false}
          sx={{
            minHeight: 40,
            height: 40,
            px: 2,
            width: { xs: "100%", sm: "auto" },
            backgroundColor: designColors.slate100,
            border: "none",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: designColors.slate100,
              border: "none",
              boxShadow: "none",
            },
          }}
        />
        <CompactHeaderSelectFilter
          dataList={departmentList}
          selectedValue={departmentName}
          updateDataFunction={updateDepartmentName}
          Icons={PersonOutlineIcon}
          staticLabel="Department"
          widthSx={{
            width: { xs: "100%", sm: "auto" },
            minWidth: { xs: "100%", sm: 220 },
          }}
        />
        {embeddedReportTypes?.length ? (
          <CompactHeaderSelectFilter
            dataList={embeddedReportTypes}
            selectedValue={selectedReportTypeOption}
            updateDataFunction={onReportTypeChange}
            Icons={PersonOutlineIcon}
            staticLabel="Report Type"
            widthSx={{
              width: { xs: "100%", sm: "auto" },
              minWidth: { xs: "100%", sm: 260 },
            }}
          />
        ) : null}
      </Mui.Stack>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      startDate,
      endDate,
      dateRangeLabel,
      departmentList,
      departmentName,
      embeddedReportTypes,
      selectedReportTypeOption,
      onReportTypeChange,
    ],
  );

  // Filters render inline in the page body (matching Employee Attendance's
  // own row below the title), not floated beside the title.
  useEffect(() => {
    if (!setReportHeaderActions) return undefined;
    setReportHeaderActions(null);
    return () => setReportHeaderActions(null);
  }, [setReportHeaderActions]);

  useEffect(() => {
    if (!customFilterNode) return undefined;
    const updateHeight = () =>
      setCustomFilterHeight(customFilterNode.offsetHeight || 0);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(customFilterNode);
    return () => observer.disconnect();
  }, [customFilterNode, visibleHeaders.length]);

  useEffect(() => {
    if (!footerNode) return undefined;
    const updateHeight = () => setFooterHeight(footerNode.offsetHeight || 0);
    updateHeight();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(footerNode);
    return () => observer.disconnect();
  }, [footerNode]);

  const renderOvertimeCell = (key, item) => {
    switch (key) {
      case "user_name":
        return (
          <Mui.Tooltip
            arrow
            slotProps={tooltipStyleProps}
            title={item?.username}
            disableHoverListener={!item?.username}
          >
            <Mui.Stack direction="row" spacing={1} alignItems="center">
              <Mui.Avatar
                sx={{ width: 28, height: 28 }}
                alt="Remy Sharp"
                src={item?.image_url ?? optionalAvathar}
              />
              <Mui.Typography
                sx={{
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  color: designColors.slate900,
                  fontSize: 14,
                }}
              >
                {item?.user_name}
              </Mui.Typography>
            </Mui.Stack>
          </Mui.Tooltip>
        );
      case "employee_id":
        return item?.employee_id;
      case "date":
        return item?.date;
      case "shift":
        return (
          <Mui.Tooltip
            arrow
            slotProps={tooltipStyleProps}
            title={item?.shift_name}
            disableHoverListener={!item?.shift_name}
          >
            <Mui.Typography
              sx={{
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                font: "inherit",
                color: "inherit",
              }}
            >
              {item?.shift ? item?.shift : "Unassigned"}
            </Mui.Typography>
          </Mui.Tooltip>
        );
      case "department_name":
        return (
          <Mui.Tooltip
            arrow
            slotProps={tooltipStyleProps}
            title={item?.department_name}
            disableHoverListener={!item?.department_name}
          >
            <Mui.Typography
              sx={{
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                font: "inherit",
                color: "inherit",
              }}
            >
              {item?.department_name}
            </Mui.Typography>
          </Mui.Tooltip>
        );
      case "overtime_start_formated":
        return item?.overtime_start_formated
          ? item?.overtime_start_formated
          : "N/A";
      case "overtime_end_formated":
        return item?.overtime_end_formated
          ? item?.overtime_end_formated
          : "N/A";
      case "overtime_break_duration":
        return item?.overtime_break_duration
          ? item?.overtime_break_duration
          : "00:00";
      case "overtime_duration":
        return item?.overtime_duration ? item?.overtime_duration : "00:00";
      case "final_approved_duration":
        return item?.final_approved_duration
          ? item?.final_approved_duration
          : "00:00";
      default:
        return null;
    }
  };

  return (
    <ThemeProvider theme={withDesignFont(theme)}>
      <FilterProvider
        domain={domain}
        userDetails={userDetails}
        subModuleId={subModuleId}
      >
        <Box sx={{ pb: 0, width: "100%", maxWidth: "100%" }}>
          {/* Filters sit in their own row directly under the page title —
              same placement as Employee Attendance Report — instead of
              floating beside the title via setReportHeaderActions. */}
          <Mui.Box sx={{ ...designShell.sectionCard, p: 1.5, mb: 2 }}>
            {headerFilterControls}
          </Mui.Box>
          <Mui.Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(4, minmax(0, 1fr))",
                xl: "repeat(4, minmax(0, 1fr))",
              },
              gap: 1.5,
              mb: 2,
            }}
          >
            {overtimeSummaryCards.map((card) => (
              <StatCard
                key={card.key}
                icon={card.icon}
                title={card.title}
                value={card.value}
                accent={card.accent}
                tint={card.tint}
              />
            ))}
          </Mui.Box>
          <Box ref={customFilterRef} sx={approveStickyCustomFilterBar}>
            <CustomFilter
              title="Overtime"
              titleOverride={titleOverride}
              filterHeaders={visibleHeaders}
              data={otRepDetails}
              filteredData={otReportData}
              onFilteredDataChange={handleFilteredDataChange}
              screenAccess={check_access}
              hideCount={true}
              isAttendanceScreenReport={true}
              showDownloadOptions={false}
              downloadFormats={["excel", "pdf", "csv"]}
              includeMetadata={true}
              metadata={getReportMetadata()}
              customDownloadHandler={handleOvertimeDownload}
              labeledToolbar={true}
              searchPlaceholder="Search by employee, department..."
            />
          </Box>

          <Box sx={{ ...designShell.sectionCard, mt: 1.5, borderRadius: 0 }}>
            {isLoading ? (
              <Loader info="Loading..." />
            ) : (
              <Mui.Box>
                <Mui.TableContainer
                  sx={{
                    boxSizing: "border-box",
                    width: "100%",
                    maxWidth: "100%",
                    overflowX: { xs: "auto", sm: "visible" },
                    overflowY: { xs: "auto", sm: "visible" },
                    pb: footerHeight ? `${footerHeight}px` : 0,
                    borderLeft: `1px solid ${designColors.slate200}`,
                    borderRight: `1px solid ${designColors.slate200}`,
                    borderBottom: `1px solid ${designColors.slate200}`,
                  }}
                >
                  <ApproveTable stickyHeader sx={{ minWidth: 900, width: "100%" }}>
                    <Mui.TableHead sx={designShell.tableStickyHead}>
                      <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                        {visibleHeaders.map((header, headerIndex) => (
                          <Mui.TableCell
                            key={header.key}
                            sx={{
                              ...approveTableHeadCell,
                              top: APP_BAR_OFFSET + customFilterHeight,
                              ...(headerIndex === 0
                                ? { borderTopLeftRadius: 0 }
                                : {}),
                              ...(headerIndex === visibleHeaders.length - 1
                                ? { borderTopRightRadius: 0 }
                                : {}),
                            }}
                          >
                            {header.name}
                          </Mui.TableCell>
                        ))}
                      </Mui.TableRow>
                    </Mui.TableHead>
                    <Mui.TableBody>
                      {otReportData?.length === 0 ? (
                        <TableEmptyState dataLength={otRepDetails} />
                      ) : (
                        otReportData
                          ?.slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage,
                          )
                          ?.map((item) => (
                            <Mui.TableRow
                              key={item?.id}
                              hover
                              sx={{
                                ...designShell.tableRowHover,
                                "&:last-child td": { borderBottom: "none" },
                                "& td": { backgroundColor: designColors.white },
                              }}
                            >
                              {visibleHeaders.map((header) => (
                                <Mui.TableCell
                                  key={header.key}
                                  sx={approveTableBodyCell}
                                >
                                  {renderOvertimeCell(header.key, item)}
                                </Mui.TableCell>
                              ))}
                            </Mui.TableRow>
                          ))
                      )}
                    </Mui.TableBody>
                  </ApproveTable>
                </Mui.TableContainer>
              </Mui.Box>
            )}

            <Mui.Box ref={footerRef} sx={approveStickyFooterBar}>
              {paginationFooter({
                page,
                rowsPerPage,
                total: otReportData?.length || 0,
                itemLabel: "records",
                onPageChange: setPage,
                onRowsPerPageChange: handleChangeRowsPerPage,
                rowsPerPageOptions,
              })}
            </Mui.Box>
          </Box>
        </Box>
      </FilterProvider>
    </ThemeProvider>
  );
};
